import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Stack,
  Alert,
  Button,
  Box,
  Divider,
} from '@mui/material';
import { CheckCircle, FlightTakeoff } from '@mui/icons-material';
import { useLocation, Link as RouterLink, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HeaderV2, FooterV2 } from '../../components/layout';

const FlightQuoteReceiptPage = () => {
  const { state } = useLocation();
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  if (!state?.requestId) return <Navigate to="/v2/flights" replace />;

  const { requestId, trackingPin, customerName, customerPhone, customerEmail, origin, destination, airlineName, flightNumber, shownAmount, shownCurrency, departureDate } = state;
  const trackUrl = `/v2/flights/track?requestId=${requestId}${customerPhone ? `&phone=${encodeURIComponent(customerPhone)}` : ''}`;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />
      <Container maxWidth="sm" sx={{ py: { xs: 8, md: 10 } }}>
        <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Request received
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Thank you, {customerName}. Our team will confirm the live IATA portal price and get back to you by phone or WhatsApp.
          </Typography>

          <Alert severity="success" sx={{ textAlign: 'left', mb: 3 }}>
            <strong>We will get back to you</strong> — usually within a few hours during business hours.
          </Alert>

          {isAuthenticated ? (
            <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
              You are already signed in. Open your <strong>Dashboard</strong> to see this quote request.
            </Alert>
          ) : (
            <Box sx={{ p: 2.5, textAlign: 'left', mb: 3, border: '1px solid', borderColor: 'primary.light', bgcolor: '#f0f7ff', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary.dark" gutterBottom>
                Track this request
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                If you already registered, use <strong>Sign in</strong> with your email and password — do not create another account.
                New guests can sign in with the tracking PIN below (or the password you chose when registering).
              </Typography>
              <Stack spacing={0.5} sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1.5, border: '1px dashed #90caf9', mb: 2 }}>
                <Typography variant="body2">
                  <strong>Login ID:</strong> {customerEmail || customerPhone}
                </Typography>
                <Typography variant="body2">
                  <strong>Tracking PIN (guests):</strong>{' '}
                  <code style={{ fontSize: '1rem', fontWeight: 700, color: '#0052cc' }}>{trackingPin}</code>
                </Typography>
              </Stack>
              <Button
                component={RouterLink}
                to="/v2/login"
                state={{
                  email: customerEmail || customerPhone,
                  pin: trackingPin,
                  info: 'Use your account password, or your tracking PIN if you booked as a guest.',
                  returnTo: '/v2/dashboard',
                }}
                variant="contained"
                color="primary"
                fullWidth
              >
                Sign in to My Dashboard
              </Button>
            </Box>
          )}

          <Stack spacing={1} sx={{ textAlign: 'left', bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
            <Typography variant="body2"><strong>Request ID:</strong> {requestId}</Typography>
            <Typography variant="body2"><strong>Tracking PIN:</strong> {trackingPin}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">
              <FlightTakeoff sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
              {origin} → {destination} · {departureDate}
            </Typography>
            <Typography variant="body2">{airlineName} {flightNumber}</Typography>
            <Typography variant="body2">
              Indicative fare: <strong>{shownCurrency} {Number(shownAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
            <Button component={RouterLink} to={trackUrl} variant="outlined" color="secondary" fullWidth>
              Track my request
            </Button>
            <Button component={RouterLink} to="/v2/flights" variant="text" fullWidth>
              Search more flights
            </Button>
          </Stack>
        </Paper>
      </Container>
      <FooterV2 />
    </Box>
  );
};

export default FlightQuoteReceiptPage;
